import React, { useCallback, useEffect, useState } from "react";
import "./availabilityCards.scss";
import Trash from "assets/Trash.svg";
import TrashDisable from "assets/Trash-Disable.svg";
import Plus from "assets/Plus.svg";
import dayjs from "dayjs";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Availability_ADD_SCHEMA,
  Inclinic_Checked,
  Home_Checked,
} from "utils/PatientPortal/Validators/AvailabilityValidation";

import { String_Initialiser } from "constant/AppConstants";

// handle*TimeChange parse the formatted string back with `format`; antd used
// to register this plugin globally, so register it explicitly here.
dayjs.extend(customParseFormat);

// antd TimePicker parity: OK-to-confirm (needConfirm), 15-minute steps, no
// "Now" button, read-only input that opens the picker on click. The callback
// keeps antd's (time, timeString) contract.
const AvailabilityTimePicker = ({ value, format, onAccept, error, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopTimePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value ? dayjs(value) : null}
        disabled={disabled}
        format={format}
        minutesStep={15}
        timeSteps={{ minutes: 15 }}
        closeOnSelect={false}
        onAccept={(time) => {
          if (time && time.isValid()) onAccept(time, time.format(format));
        }}
        slotProps={{
          textField: {
            size: "small",
            error: Boolean(error),
            onClick: () => !disabled && setOpen(true),
            readOnly: true,
          },
          actionBar: { actions: ["accept"] },
        }}
      />
    </LocalizationProvider>
  );
};

export const AvailabilityCard = ({
  heading,
  locationData,
  locations,
  forwardedRef,
  dayOfWeek,
}) => {
  const [validations, setValidations] = useState([]);
  const [checkBoxValidation, setCheckBoxValidation] = useState([]);
  const [startTimestatus, setStartTimeStatus] = useState([]);
  const format = "hh:mm a";

  const handleAddLocation = () => {
    locations([
      ...locationData,
      {
        dayOfWeek: dayOfWeek,
        startTime: String_Initialiser,
        endTime: String_Initialiser,
        locationInput: String_Initialiser,
      },
    ]);
  };
  const handleDeleteLocation = (index) => {
    const updatedLocations = locationData.filter((_, i) => i !== index);
    locations(updatedLocations);

    if (validations) {
      const updatedValidation = validations.filter((_, i) => i !== index);
      setValidations(updatedValidation);
    }
  };

  const handleInputChange = (index, key, value) => {
    const updatedLocations = [...locationData];
    updatedLocations[index][key] = value;
    locations(updatedLocations);
  };

  const handleRadioChange = (index, option) => {
    const validationArray = locationData.map((location, index) => {
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });
    setCheckBoxValidation((prevStatus) => ({
      ...prevStatus,
      [index]: validationArray[index],
    }));
    const updatedLocations = [...locationData];
    updatedLocations[index].locationInput = option;
    locations(updatedLocations);
  };

  const handleStartTimeChange = (time, timeString, index) => {
    const validationArray = locationData.map((location, index) => {
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });
    setStartTimeStatus((prevStatus) => ({
      ...prevStatus,
      [index]: validationArray[index],
    }));
    const momentTime = dayjs(timeString, format);

    if (!locationData[index].endTime) {
      const endTime = momentTime.add(15, "minute");
      handleInputChange(index, "endTime", endTime);
    }

    handleInputChange(index, "startTime", momentTime);
  };

  const handleEndTimeChange = (time, timeString, index) => {
    const validationArray = locationData.map((location, index) => {
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });
    setStartTimeStatus((prevStatus) => ({
      ...prevStatus,
      [index]: validationArray[index],
    }));
    const momentTime = dayjs(timeString, format);
    handleInputChange(index, "endTime", momentTime);
  };

  const handleValidate = useCallback(() => {
    const validationArray = locationData.map((location, index) => {
      if (
        (location.startTime === String_Initialiser ||
          location.startTime === undefined ||
          location.startTime === null) &&
        (location.endTime === String_Initialiser ||
          location.endTime === undefined ||
          location.endTime === null) &&
        (location.locationInput === String_Initialiser ||
          location.locationInput === undefined ||
          location.locationInput === null)
      ) {
        return { validation: Availability_ADD_SCHEMA.allFields };
      } else if (
        location.locationInput === String_Initialiser ||
        location.locationInput === null
      ) {
        return { validation: Availability_ADD_SCHEMA.locationFields };
      } else if (
        location.startTime === String_Initialiser ||
        location.endTime === String_Initialiser ||
        location.startTime === null ||
        location.endTime === null
      ) {
        return { validation: Availability_ADD_SCHEMA.timeFieldsStartAndEnd };
      } else if (location.endTime.isBefore(location.startTime)) {
        return { validation: Availability_ADD_SCHEMA.timeFieldEnd };
      } else if (location.endTime.isSame(location.startTime)) {
        return { validation: Availability_ADD_SCHEMA.timeFieldSame };
      }
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });

    setValidations(validationArray);
    setCheckBoxValidation(validationArray);
    setStartTimeStatus(validationArray);

    const anyValidationNotEmpty = validationArray.some(
      (val) => val.validation !== String_Initialiser
    );

    return !anyValidationNotEmpty;
  }, [locationData]);

  useEffect(() => {
    forwardedRef.current = {
      handleValidate: handleValidate,
    };
  }, [handleValidate, forwardedRef]);

  return (
    <div className="Container">
      <span className="Heading">{heading}</span>
      {locationData?.map((location, index) => {
        return (
          <div className="Inner-main-container" key={location.id || index}>
            <div className="Inner-container" key={index}>
              <div className="Inner-main">
                <div className="Inner-text">
                  <span className="Inner-text-Heading">Location</span>
                  <div className="Inner-text-label">
                    <div className="Inner-text-checkbox">
                      <FormControlLabel
                        label="In Clinic"
                        control={
                          <Checkbox
                            size="small"
                            name={`locationRadio_${index}`}
                            value={3}
                            checked={location.locationInput === 3}
                            onChange={() => handleRadioChange(index, 3)}
                            className={
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.locationFields ||
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.allFields
                                ? "error-checkbox"
                                : String_Initialiser
                            }
                          />
                        }
                      />
                    </div>
                    <div className="Inner-text-checkbox">
                      <FormControlLabel
                        label="Home"
                        control={
                          <Checkbox
                            size="small"
                            name={`locationRadio_${index}`}
                            value={5}
                            checked={location.locationInput === 5}
                            onChange={() => handleRadioChange(index, 5)}
                            className={
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.locationFields ||
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.allFields
                                ? "error-checkbox"
                                : String_Initialiser
                            }
                          />
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="Inner-second-feild">
                  <div className="Inner-text-startTime">
                    <span className="Inner-span-text">Start time</span>
                    <AvailabilityTimePicker
                      value={location.startTime}
                      format={format}
                      onAccept={(time, timeString) =>
                        handleStartTimeChange(time, timeString, index)
                      }
                      error={
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldsStartAndEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldWithin ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldOverlaps ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.allFields
                      }
                    />
                  </div>
                  <div className="Inner-text-startTime">
                    <span className="Inner-span-text-end">End time</span>
                    <AvailabilityTimePicker
                      value={location.endTime}
                      format={format}
                      onAccept={(time, timeString) =>
                        handleEndTimeChange(time, timeString, index)
                      }
                      error={
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldsStartAndEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldWithin ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldOverlaps ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.allFields
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="Trash-button-main">
                <img
                  className="Trash-button"
                  src={Trash}
                  alt={String_Initialiser}
                  onClick={() => handleDeleteLocation(index)}
                />
              </div>
            </div>

            {validations &&
              validations.length > 0 &&
              validations[index]?.validation !== String_Initialiser && (
                <div className="validation">
                  {validations[index]?.validation}
                </div>
              )}
          </div>
        );
      })}

      <div className="Add-more" onClick={handleAddLocation}>
        <img src={Plus} alt="Loading" className="Add-more-img"></img>
        <span className="Add-more-text">Add more available time</span>
      </div>
    </div>
  );
};

export const EditAvailabilityCard = ({
  heading,
  locationData,
  locations,
  forwardedRef,
  dayOfWeek,
}) => {
  const [validations, setValidations] = useState([]);
  const [checkBoxValidation, setCheckBoxValidation] = useState([]);
  const [startTimestatus, setStartTimeStatus] = useState([]);
  const format = "hh:mm a";

  const handleAddLocation = () => {
    locations([
      ...locationData,
      {
        dayOfWeek: dayOfWeek,
        startTime: String_Initialiser,
        endTime: String_Initialiser,
        locationInput: String_Initialiser,
        isDisabled: false,
      },
    ]);
  };
  const handleDeleteLocation = (index) => {
    const updatedLocations = locationData.filter((_, i) => i !== index);
    locations(updatedLocations);

    if (validations) {
      const updatedValidation = validations.filter((_, i) => i !== index);
      setValidations(updatedValidation);
    }
  };

  const handleInputChange = (index, key, value) => {
    const updatedLocations = [...locationData];
    updatedLocations[index][key] = value;
    locations(updatedLocations);
  };

  const handleRadioChange = (index, option) => {
    const validationArray = locationData.map((location, index) => {
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });
    setCheckBoxValidation((prevStatus) => ({
      ...prevStatus,
      [index]: validationArray[index],
    }));
    const updatedLocations = [...locationData];
    updatedLocations[index].locationInput = option;
    locations(updatedLocations);
  };

  const handleStartTimeChange = (time, timeString, index) => {
    const validationArray = locationData.map((location, index) => {
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });
    setStartTimeStatus((prevStatus) => ({
      ...prevStatus,
      [index]: validationArray[index],
    }));
    const momentTime = dayjs(timeString, format);

    if (!locationData[index].endTime) {
      const endTime = momentTime.add(15, "minute");
      handleInputChange(index, "endTime", endTime);
    }

    handleInputChange(index, "startTime", momentTime);
  };

  const handleEndTimeChange = (time, timeString, index) => {
    const validationArray = locationData.map((location, index) => {
      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });
    setStartTimeStatus((prevStatus) => ({
      ...prevStatus,
      [index]: validationArray[index],
    }));
    const momentTime = dayjs(timeString, format);
    handleInputChange(index, "endTime", momentTime);
  };

  const handleValidate = useCallback(() => {
    const validationArray = locationData.map((location, index) => {
      if (
        (location.startTime === String_Initialiser ||
          location.startTime === undefined ||
          location.startTime === null) &&
        (location.endTime === String_Initialiser ||
          location.endTime === undefined ||
          location.endTime === null) &&
        (location.locationInput === String_Initialiser ||
          location.locationInput === undefined ||
          location.locationInput === null)
      ) {
        return { validation: Availability_ADD_SCHEMA.allFields };
      } else if (
        location.locationInput === String_Initialiser ||
        location.locationInput === null
      ) {
        return { validation: Availability_ADD_SCHEMA.locationFields };
      } else if (
        location.startTime === String_Initialiser ||
        location.endTime === String_Initialiser ||
        location.startTime === null ||
        location.endTime === null
      ) {
        return { validation: Availability_ADD_SCHEMA.timeFieldsStartAndEnd };
      } else if (location.endTime.isBefore(location.startTime)) {
        return { validation: Availability_ADD_SCHEMA.timeFieldEnd };
      } else if (location.endTime.isSame(location.startTime)) {
        return { validation: Availability_ADD_SCHEMA.timeFieldSame };
      }

      return { validation: Availability_ADD_SCHEMA.successfullValidation };
    });

    setValidations(validationArray);
    setCheckBoxValidation(validationArray);
    setStartTimeStatus(validationArray);

    const anyValidationNotEmpty = validationArray.some(
      (val) => val.validation !== String_Initialiser
    );

    return !anyValidationNotEmpty;
  }, [locationData]);

  useEffect(() => {
    forwardedRef.current = {
      handleValidate: handleValidate,
    };
  }, [handleValidate, forwardedRef]);

  return (
    <div className="Container">
      <span className="Heading">{heading}</span>
      {locationData?.map((location, index) => {
        return (
          <div className="Inner-main-container" key={location.id || index}>
            <div className="Inner-container" key={index}>
              <div className="Inner-main">
                <div className="Inner-text">
                  <span className="Inner-text-Heading">Location</span>
                  <div className="Inner-text-label">
                    <div className="Inner-text-checkbox">
                      <FormControlLabel
                        label="In Clinic"
                        control={
                          <Checkbox
                            size="small"
                            name={`locationRadio_${index}`}
                            value={3}
                            checked={
                              Inclinic_Checked.includes(location.location) ||
                              Inclinic_Checked.includes(location.locationInput)
                            }
                            onChange={() => handleRadioChange(index, 3)}
                            className={
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.locationFields ||
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.allFields
                                ? "error-checkbox"
                                : String_Initialiser
                            }
                            disabled={location.isDisabled}
                          />
                        }
                      />
                    </div>
                    <div className="Inner-text-checkbox">
                      <FormControlLabel
                        label="Home"
                        control={
                          <Checkbox
                            size="small"
                            name={`locationRadio_${index}`}
                            value={5}
                            checked={
                              Home_Checked.includes(location.location) ||
                              Home_Checked.includes(location.locationInput)
                            }
                            onChange={() => handleRadioChange(index, 5)}
                            className={
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.locationFields ||
                              checkBoxValidation[index]?.validation ===
                                Availability_ADD_SCHEMA.allFields
                                ? "error-checkbox"
                                : String_Initialiser
                            }
                            disabled={location.isDisabled}
                          />
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="Inner-second-feild">
                  <div className="Inner-text-startTime">
                    <span className="Inner-span-text">Start time</span>
                    <AvailabilityTimePicker
                      value={location.startTime}
                      format={format}
                      disabled={location.isDisabled}
                      onAccept={(time, timeString) =>
                        handleStartTimeChange(time, timeString, index)
                      }
                      error={
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldsStartAndEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldWithin ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldOverlaps ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.allFields
                      }
                    />
                  </div>
                  <div className="Inner-text-startTime">
                    <span className="Inner-span-text-end">End time</span>
                    <AvailabilityTimePicker
                      value={location.endTime}
                      format={format}
                      disabled={location.isDisabled}
                      onAccept={(time, timeString) =>
                        handleEndTimeChange(time, timeString, index)
                      }
                      error={
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldsStartAndEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldWithin ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.timeFieldEnd ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.TimeFieldOverlaps ||
                        startTimestatus[index]?.validation ===
                          Availability_ADD_SCHEMA.allFields
                      }
                    />
                  </div>
                </div>
              </div>
              {location.isDisabled ? (
                <div className="Trash-button-main-disable">
                  <img className="Trash-button" src={TrashDisable} alt={String_Initialiser} />
                </div>
              ) : (
                <div className="Trash-button-main">
                  <img
                    className="Trash-button"
                    src={Trash}
                    alt={String_Initialiser}
                    onClick={() => handleDeleteLocation(index)}
                  />
                </div>
              )}
            </div>

            {validations &&
              validations.length > 0 &&
              validations[index]?.validation !== String_Initialiser && (
                <div className="validation">
                  {validations[index]?.validation}
                </div>
              )}
          </div>
        );
      })}

      <div className="Add-more" onClick={handleAddLocation}>
        <img src={Plus} alt="Loading" className="Add-more-img"></img>
        <span className="Add-more-text">Add more available time</span>
      </div>
    </div>
  );
};
